import { getter, commit, dispatch, watch } from './store';
import toasted from './toasted';
import BaseUtils from './base';

const HAS_ROLE = 'hasRole';
const PERMIT = 'permit';
const DENY = 'deny';

/**
 * Permet d'avoir une syntaxe de route avec
 * meta:{
 *    auth:'permit' || 'hasRole(ROLE_XXX)'
 * }
 *
 *  Les routes sans rien sont en 'deny'
 */
class AccessControl {
  constructor(input, auth) {
    this.auth = auth;
    const withRole = input.match(/hasRole\((.*)\)/);
    if (withRole) {
      this.check = HAS_ROLE;
      this.requiredRole = withRole[1];
    } else if (input === PERMIT) {
      this.check = PERMIT;
    } else {
      this.check = DENY;
    }
  }

  isPermitted() {
    return this.check === PERMIT;
  }

  isDenied() {
    return this.check === DENY;
  }

  isGranted() {
    const granted =
      this.check === HAS_ROLE && this.auth.hasRole(this.requiredRole);
    return granted;
  }

  denied() {
    toasted.error('auth.accessDenied');
  }
}

class Oauth extends BaseUtils {
  hasRole(role) {
    const authorities = getter('oauth/grantedAuthorities');
    if (authorities) {
      const roles = role.split(', ');
      return roles.some(r => authorities.includes(r));
    }
    return false;
  }

  /**
   * Création de l'acces control
   */
  computeAccessControl(to) {
    if (to.meta && to.meta.auth) {
      return new AccessControl(to.meta.auth, this);
    }
    return new AccessControl(DENY);
  }

  /**
   * Permet de savoir si l'on a le droit de naviguer vers la route
   */
  mayNavigateTo(to) {
    const route = this.vue().$router.resolve(to);
    if (route && route.resolved) {
      const ac = this.computeAccessControl(route.resolved);
      return ac.isPermitted() || ac.isGranted();
    }
    return false;
  }

  userid() {
    return getter('oauth/userid');
  }

  accessToken() {
    return getter('oauth/access_token');
  }

  username() {
    return getter('oauth/username');
  }

  nomComplet() {
    return getter('oauth/nomComplet');
  }

  logged() {
    return getter('oauth/logged');
  }

  expired() {
    return getter('oauth/expired')();
  }

  loggedButExpired() {
    return this.logged() && this.expired();
  }

  login(login, password, notif = true) {
    commit('loader/work', 1);
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('username', login);
    params.append('password', password);
    return this.vue()
      .$http.post('/oauth/token', params, {
        headers: {
          Authorization: 'Basic ' + btoa('agil:agil'),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => {
        commit('loader/work', -1);
        commit('oauth/token', response.body);
        return this.vue()
          .$http.get('/oauth/principal', {
            headers: { Authorization: 'Bearer ' + response.body.access_token },
          })
          .then(response => {
            if (notif) {
              toasted.info('auth.connected');
            }
            commit('oauth/user', response.body);
          });
      })
      .catch(response => {
        if (response.status === 400) {
          toasted.error('auth.unauthorized');
        } else {
          toasted.error('auth.error');
        }
      })
      .finally(() => commit('loader/work', -1));
  }

  requireLogin(to) {
    dispatch('oauth/loginAndRedirect', to);
  }

  refreshToken() {
    const refreshToken = getter('oauth/refresh_token');
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);
    return this.vue()
      .$http.post('/oauth/token', params, {
        headers: {
          Authorization: 'Basic ' + btoa('agil:agil'),
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(response => commit('oauth/token', response.body))
      .catch(e => {
        this.logout();
        throw e;
      });
  }

  logout() {
    toasted.info('auth.disconnected');
    commit('oauth/token', null);
    commit('oauth/user', null);
  }

  renewAccessToken(promiseBuilder) {
    if (this.loggedButExpired()) {
      return this.refreshToken().then(promiseBuilder);
    } else {
      return promiseBuilder();
    }
  }

  whenLogged(promiseBuilder) {
    if (this.logged()) {
      this.renewAccessToken(() => promiseBuilder(true));
    }

    watch(() => getter('oauth/logged'), promiseBuilder);
  }
}

const instance = new Oauth();

// expore l'instance par défaut
export default instance;
